import { Type, type Static } from "@sinclair/typebox";
import { TemporaryFileReferenceSchema } from "./document-platform.ts";

export const PPT_EXPERT_AGENT_ID = "a744869d-41fd-49cf-af58-a83c757fc23c" as const;
export const PPT_EXPERT_AGENT_KEY = "ppt-expert" as const;
export const PPT_EXPERT_DISPLAY_NAME = "PPT 专家" as const;
export const PPT_EXPERT_DESCRIPTION =
  "帮你制作和修改演示文稿，生成可继续编辑的 PowerPoint 文件。" as const;
export const PPT_EXPERT_MATRIX_LOCALPART = "ppt-expert" as const;

export const PPT_EXPERT_TOOL_KEYS = Object.freeze({
  listThemes: "list_presentation_themes",
  open: "open_presentation",
  setOutline: "set_presentation_outline",
  draftPages: "draft_presentation_pages",
  revisePages: "revise_presentation_pages",
  commitPages: "commit_presentation_pages",
  inspectSlide: "inspect_presentation_slide",
  check: "check_presentation"
} as const);

export const PresentationCheckpointHintSchema = Type.Object(
  {
    temporaryFile: TemporaryFileReferenceSchema,
    sourceHash: Type.String({ pattern: "^[a-f0-9]{64}$" })
  },
  { additionalProperties: false }
);

const IdentifierSchema = Type.String({ minLength: 1, maxLength: 128 });
const PageSourceTextSchema = Type.String({ minLength: 1, maxLength: 65_536 });
const ThemeDiscoveryTextSchema = Type.String({ minLength: 1, maxLength: 128 });
const ThemeDiscoveryTextsSchema = Type.Array(ThemeDiscoveryTextSchema, {
  minItems: 1,
  maxItems: 32
});
const ThemeEmphasisSchema = Type.Union([
  Type.Literal("low"),
  Type.Literal("medium"),
  Type.Literal("high")
]);
const ThemeColorModeSchema = Type.Union([Type.Literal("light"), Type.Literal("dark")]);
const PresentationDeliveryModeSchema = Type.Union([
  Type.Literal("presenter-led"),
  Type.Literal("reader-led"),
  Type.Literal("hybrid")
]);
const PresentationDesignReferenceSourceSchema = Type.String({
  minLength: 8,
  maxLength: 1_024,
  pattern:
    "^/(?:input/[^/\\\\]+|tmp/files/[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[1-5][0-9a-fA-F]{3}-[89aAbB][0-9a-fA-F]{3}-[0-9a-fA-F]{12}/[^/\\\\]+)\\.(?:png|jpe?g)$"
});
const PresentationThemeRefSchema = Type.Object(
  { id: IdentifierSchema, variant: IdentifierSchema },
  { additionalProperties: false }
);
const PresentationThemeVariantSchema = Type.Object(
  {
    id: IdentifierSchema,
    name: Type.String({ minLength: 1, maxLength: 128 }),
    summary: Type.String({ minLength: 1, maxLength: 512 }),
    visualCharacter: ThemeDiscoveryTextsSchema,
    density: ThemeEmphasisSchema,
    colorMode: ThemeColorModeSchema
  },
  { additionalProperties: false }
);
const PresentationThemeDescriptorSchema = Type.Object(
  {
    id: IdentifierSchema,
    name: Type.String({ minLength: 1, maxLength: 128 }),
    summary: Type.String({ minLength: 1, maxLength: 512 }),
    bestFor: ThemeDiscoveryTextsSchema,
    avoidFor: Type.Optional(ThemeDiscoveryTextsSchema),
    defaultVariant: IdentifierSchema,
    variants: Type.Array(PresentationThemeVariantSchema, { minItems: 1, maxItems: 16 })
  },
  { additionalProperties: false }
);
export const ListPresentationThemesInputSchema = Type.Object({}, { additionalProperties: false });
export const ListPresentationThemesResultSchema = Type.Object(
  { themes: Type.Array(PresentationThemeDescriptorSchema, { minItems: 1, maxItems: 16 }) },
  { additionalProperties: false }
);

const TextStyleNameSchema = Type.Union([
  Type.Literal("display"),
  Type.Literal("title-lg"),
  Type.Literal("title-md"),
  Type.Literal("title-sm"),
  Type.Literal("heading-lg"),
  Type.Literal("heading-md"),
  Type.Literal("heading-sm"),
  Type.Literal("body-lg"),
  Type.Literal("body-md"),
  Type.Literal("body-sm"),
  Type.Literal("label"),
  Type.Literal("caption"),
  Type.Literal("footnote"),
  Type.Literal("metric-xl"),
  Type.Literal("metric-lg"),
  Type.Literal("metric-md")
]);
const StrokeStyleSchema = Type.Object(
  {
    width: Type.Number({ exclusiveMinimum: 0 }),
    color: IdentifierSchema
  },
  { additionalProperties: false }
);
export const PresentationThemeGuideSchema = Type.Object(
  {
    id: IdentifierSchema,
    variant: IdentifierSchema,
    name: IdentifierSchema,
    summary: Type.String({ minLength: 1, maxLength: 512 }),
    colorMode: ThemeColorModeSchema,
    density: ThemeEmphasisSchema,
    colors: Type.Record(IdentifierSchema, Type.String({ pattern: "^#[0-9A-F]{6}$" })),
    textStyles: Type.Array(
      Type.Object(
        {
          name: TextStyleNameSchema,
          fontFace: Type.String({ minLength: 1, maxLength: 128 }),
          fontEastAsian: Type.String({ minLength: 1, maxLength: 128 }),
          fontSize: Type.Number({ exclusiveMinimum: 0 }),
          lineHeight: Type.Number({ exclusiveMinimum: 0 }),
          bold: Type.Boolean(),
          color: IdentifierSchema
        },
        { additionalProperties: false }
      ),
      { minItems: 16, maxItems: 16 }
    ),
    strokes: Type.Object(
      { hairline: StrokeStyleSchema, default: StrokeStyleSchema, strong: StrokeStyleSchema },
      { additionalProperties: false }
    ),
    radii: Type.Object(
      {
        none: Type.Number({ minimum: 0 }),
        sm: Type.Number({ minimum: 0 }),
        md: Type.Number({ minimum: 0 }),
        lg: Type.Number({ minimum: 0 })
      },
      { additionalProperties: false }
    ),
    shadows: Type.Array(Type.Union([Type.Literal("soft"), Type.Literal("raised")]), {
      maxItems: 2
    }),
    chart: Type.Object(
      {
        palette: Type.Array(IdentifierSchema, { minItems: 1, maxItems: 16 }),
        textStyle: TextStyleNameSchema
      },
      { additionalProperties: false }
    ),
    table: Type.Object(
      { headerTextStyle: TextStyleNameSchema, bodyTextStyle: TextStyleNameSchema },
      { additionalProperties: false }
    )
  },
  { additionalProperties: false }
);

const AuthoringGuideRuleSchema = Type.String({ minLength: 1, maxLength: 512 });
export const PresentationAuthoringGuideSchema = Type.Object(
  {
    sourceModel: Type.String({ minLength: 1, maxLength: 1_024 }),
    allowedSyntax: Type.Array(AuthoringGuideRuleSchema, { minItems: 1, maxItems: 32 }),
    forbiddenSyntax: Type.Array(AuthoringGuideRuleSchema, { minItems: 1, maxItems: 32 }),
    editingRules: Type.Array(AuthoringGuideRuleSchema, { minItems: 1, maxItems: 32 }),
    slideExample: Type.String({ minLength: 1, maxLength: 8_192 })
  },
  { additionalProperties: false }
);

export const OpenPresentationInputSchema = Type.Union([
  Type.Object(
    {
      mode: Type.Literal("new"),
      title: Type.String({ minLength: 1, maxLength: 256 }),
      themeId: IdentifierSchema,
      themeVariant: IdentifierSchema,
      format: Type.Union([Type.Literal("wide"), Type.Literal("standard")]),
      deliveryMode: PresentationDeliveryModeSchema
    },
    { additionalProperties: false }
  ),
  Type.Object({ mode: Type.Literal("resume") }, { additionalProperties: false })
]);

const PresentationSlideSummarySchema = Type.Object(
  {
    slideNumber: Type.Integer({ minimum: 1, maximum: 60 }),
    sectionNumber: Type.Optional(Type.Integer({ minimum: 1, maximum: 60 })),
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    imageCount: Type.Integer({ minimum: 0, maximum: 240 })
  },
  { additionalProperties: false }
);

const PresentationOutlineTextSchema = Type.String({ minLength: 1, maxLength: 2_000 });
export const PresentationOutlineSnapshotSchema = Type.Object(
  {
    brief: Type.Object(
      {
        topic: PresentationOutlineTextSchema,
        audience: PresentationOutlineTextSchema,
        purpose: Type.Union([
          Type.Literal("inform"),
          Type.Literal("persuade"),
          Type.Literal("recommend"),
          Type.Literal("decide"),
          Type.Literal("teach")
        ]),
        contentStrategy: Type.Union([
          Type.Literal("preserve"),
          Type.Literal("restructure"),
          Type.Literal("expand")
        ]),
        desiredOutcome: PresentationOutlineTextSchema,
        centralTakeaway: PresentationOutlineTextSchema,
        constraints: Type.Optional(
          Type.Object(
            {
              durationMinutes: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_440 })),
              sourcePolicy: Type.Optional(
                Type.Union([
                  Type.Literal("provided-only"),
                  Type.Literal("authorized-knowledge"),
                  Type.Literal("general")
                ])
              )
            },
            { additionalProperties: false }
          )
        )
      },
      { additionalProperties: false }
    ),
    design: Type.Object(
      {
        direction: PresentationOutlineTextSchema,
        deckStylePreset: Type.String({ minLength: 1, maxLength: 64 }),
        density: Type.Union([
          Type.Literal("sparse"),
          Type.Literal("balanced"),
          Type.Literal("dense")
        ])
      },
      { additionalProperties: false }
    ),
    narrative: Type.Object(
      {
        arc: PresentationOutlineTextSchema,
        sections: Type.Array(
          Type.Object(
            {
              title: Type.String({ minLength: 1, maxLength: 200 }),
              objective: PresentationOutlineTextSchema,
              keyPoints: Type.Array(Type.String({ minLength: 1, maxLength: 500 }), {
                minItems: 1,
                maxItems: 24
              }),
              evidence: Type.Optional(
                Type.Array(Type.String({ minLength: 1, maxLength: 500 }), { maxItems: 24 })
              ),
              pageCount: Type.Integer({ minimum: 1, maximum: 60 })
            },
            { additionalProperties: false }
          ),
          { minItems: 1, maxItems: 20 }
        )
      },
      { additionalProperties: false }
    ),
    assumptions: Type.Optional(
      Type.Array(Type.String({ minLength: 1, maxLength: 500 }), { maxItems: 24 })
    ),
    openQuestions: Type.Optional(
      Type.Array(
        Type.Object(
          {
            question: PresentationOutlineTextSchema,
            impact: Type.Union([
              Type.Literal("blocking"),
              Type.Literal("material"),
              Type.Literal("optional")
            ]),
            options: Type.Optional(
              Type.Array(
                Type.Object(
                  {
                    label: Type.String({ minLength: 1, maxLength: 200 }),
                    description: Type.Optional(Type.String({ minLength: 1, maxLength: 500 }))
                  },
                  { additionalProperties: false }
                ),
                { minItems: 2, maxItems: 4 }
              )
            )
          },
          { additionalProperties: false }
        ),
        { maxItems: 16 }
      )
    )
  },
  { additionalProperties: false }
);

export const OpenPresentationResultSchema = Type.Union([
  Type.Object(
    {
      status: Type.Literal("opened"),
      origin: Type.Union([Type.Literal("checkpoint"), Type.Literal("empty")]),
      title: Type.String({ minLength: 1, maxLength: 256 }),
      format: Type.Union([Type.Literal("wide"), Type.Literal("standard")]),
      deliveryMode: PresentationDeliveryModeSchema,
      themeGuide: PresentationThemeGuideSchema,
      outline: Type.Optional(PresentationOutlineSnapshotSchema),
      slides: Type.Array(PresentationSlideSummarySchema, { maxItems: 60 })
    },
    { additionalProperties: false }
  ),
  Type.Object({ status: Type.Literal("not_found") }, { additionalProperties: false })
]);

const GeneratedPresentationDesignReferenceSchema = Type.Object(
  {
    status: Type.Literal("generated"),
    role: Type.Literal("heuristic"),
    reproduction: Type.Literal("best_effort"),
    renderer: Type.Literal("image-model"),
    mediaType: Type.Union([Type.Literal("image/png"), Type.Literal("image/jpeg")]),
    width: Type.Integer({ minimum: 1, maximum: 8_192 }),
    height: Type.Integer({ minimum: 1, maximum: 8_192 })
  },
  { additionalProperties: false }
);
const ProvidedPresentationDesignReferenceSchema = Type.Object(
  {
    status: Type.Literal("provided"),
    role: Type.Literal("design-reference"),
    reproduction: Type.Literal("best_effort"),
    mediaType: Type.Union([Type.Literal("image/png"), Type.Literal("image/jpeg")]),
    width: Type.Integer({ minimum: 1, maximum: 8_192 }),
    height: Type.Integer({ minimum: 1, maximum: 8_192 })
  },
  { additionalProperties: false }
);
const UnavailablePresentationDesignReferenceSchema = Type.Object(
  {
    status: Type.Literal("unavailable"),
    role: Type.Literal("heuristic"),
    reproduction: Type.Literal("best_effort"),
    renderer: Type.Literal("image-model"),
    reason: Type.Union([
      Type.Literal("generator_unavailable"),
      Type.Literal("generation_failed"),
      Type.Literal("output_invalid"),
      Type.Literal("dimension_mismatch")
    ])
  },
  { additionalProperties: false }
);
export const PresentationDesignReferenceSchema = Type.Union([
  GeneratedPresentationDesignReferenceSchema,
  ProvidedPresentationDesignReferenceSchema,
  UnavailablePresentationDesignReferenceSchema
]);

const SlideNumberSchema = Type.Integer({ minimum: 1, maximum: 60 });
const AssignmentTextSchema = Type.String({ minLength: 1, maxLength: 2_000, pattern: "\\S" });
const PresentationPageEvidenceSchema = Type.Object(
  {
    statement: AssignmentTextSchema,
    sourceRef: Type.Optional(
      Type.String({
        minLength: 1,
        maxLength: 1_024,
        description:
          "Fact provenance locator only; it may include a section or fragment and never authorizes Page asset insertion."
      })
    )
  },
  { additionalProperties: false }
);
const PresentationPageAssetSourcesSchema = Type.Array(
  Type.String({ minLength: 1, maxLength: 1_024, pattern: "\\S" }),
  {
    minItems: 1,
    maxItems: 16,
    uniqueItems: true,
    description:
      "Exact authorized sources for assets the Page Worker may insert. Current-message files must use their complete /input/<name> alias without a section or fragment."
  }
);
export const PresentationPageCreateAssignmentSchema = Type.Object(
  {
    operation: Type.Literal("create"),
    targetSlideNumber: SlideNumberSchema,
    objective: AssignmentTextSchema,
    claim: AssignmentTextSchema,
    designReferenceSourceRef: Type.Optional(PresentationDesignReferenceSourceSchema),
    pageAssetSourceRefs: Type.Optional(PresentationPageAssetSourcesSchema),
    evidence: Type.Array(PresentationPageEvidenceSchema, { maxItems: 32 })
  },
  { additionalProperties: false }
);
export const PresentationPageReviseAssignmentSchema = Type.Object(
  {
    operation: Type.Literal("revise"),
    sourceSlideNumber: SlideNumberSchema,
    targetSlideNumber: SlideNumberSchema,
    instruction: AssignmentTextSchema,
    designReferenceSourceRef: Type.Optional(PresentationDesignReferenceSourceSchema),
    pageAssetSourceRefs: Type.Optional(PresentationPageAssetSourcesSchema),
    evidence: Type.Optional(Type.Array(PresentationPageEvidenceSchema, { maxItems: 32 }))
  },
  { additionalProperties: false }
);
export const PresentationPageAssignmentSchema = Type.Union([
  PresentationPageCreateAssignmentSchema,
  PresentationPageReviseAssignmentSchema
]);
export const PresentationPageRetainItemSchema = Type.Object(
  {
    operation: Type.Literal("retain"),
    sourceSlideNumber: SlideNumberSchema,
    targetSlideNumber: SlideNumberSchema
  },
  { additionalProperties: false }
);
export const PresentationPageBatchItemSchema = Type.Union([
  PresentationPageCreateAssignmentSchema,
  PresentationPageReviseAssignmentSchema,
  PresentationPageRetainItemSchema
]);
export const DraftPresentationPagesInputSchema = Type.Object(
  { pages: Type.Array(PresentationPageBatchItemSchema, { minItems: 1, maxItems: 60 }) },
  { additionalProperties: false }
);
export const RevisePresentationPagesInputSchema = Type.Object(
  {
    revisions: Type.Array(
      Type.Object(
        {
          targetSlideNumber: SlideNumberSchema,
          feedback: Type.String({ minLength: 1, maxLength: 4_096, pattern: "\\S" })
        },
        { additionalProperties: false }
      ),
      { minItems: 1, maxItems: 60 }
    )
  },
  { additionalProperties: false }
);
export const CommitPresentationPagesInputSchema = Type.Object({}, { additionalProperties: false });

export const InspectPresentationSlideInputSchema = Type.Object(
  { slideNumber: Type.Integer({ minimum: 1, maximum: 60 }) },
  { additionalProperties: false }
);

export const PresentationCompilerDiagnosticSchema = Type.Object(
  {
    code: Type.String({ minLength: 1, maxLength: 32, pattern: "^[A-Z0-9]+$" }),
    severity: Type.Union([Type.Literal("error"), Type.Literal("warning"), Type.Literal("info")]),
    message: Type.String({ minLength: 1, maxLength: 1_024 }),
    line: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 })),
    column: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 })),
    slideNumber: Type.Optional(Type.Integer({ minimum: 1, maximum: 10_000 })),
    hint: Type.Optional(Type.String({ minLength: 1, maxLength: 1_024 })),
    validOptions: Type.Optional(Type.Array(IdentifierSchema, { maxItems: 32 }))
  },
  { additionalProperties: false }
);
const DiagnosticsSchema = Type.Array(PresentationCompilerDiagnosticSchema, { maxItems: 64 });

export const PresentationCurrentStateSchema = Type.Object(
  {
    theme: PresentationThemeRefSchema,
    format: Type.Union([Type.Literal("wide"), Type.Literal("standard")]),
    deliveryMode: PresentationDeliveryModeSchema,
    pageCount: Type.Integer({ minimum: 0, maximum: 60 }),
    slides: Type.Array(PresentationSlideSummarySchema, { maxItems: 60 })
  },
  { additionalProperties: false }
);

export const CheckPresentationInputSchema = Type.Object({}, { additionalProperties: false });
export const CheckPresentationResultSchema = Type.Object(
  {
    status: Type.Union([Type.Literal("valid"), Type.Literal("invalid")]),
    diagnostics: DiagnosticsSchema,
    currentState: PresentationCurrentStateSchema,
    omittedDiagnosticCount: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 }))
  },
  { additionalProperties: false }
);

const PageDraftSummarySchema = Type.Object(
  {
    title: Type.Optional(Type.String({ minLength: 1, maxLength: 500 })),
    imageCount: Type.Integer({ minimum: 0, maximum: 240 }),
    objectTypes: Type.Array(Type.String({ minLength: 1, maxLength: 128 }), { maxItems: 32 })
  },
  { additionalProperties: false }
);
export const CompiledScreenshotSchema = Type.Union([
  Type.Object(
    { status: Type.Literal("rendered"), mediaType: Type.Literal("image/png") },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      status: Type.Literal("unavailable"),
      code: Type.Union([
        Type.Literal("RENDERER_FAILED"),
        Type.Literal("DEADLINE_EXCEEDED"),
        Type.Literal("RESOURCE_LIMIT_EXCEEDED")
      ])
    },
    { additionalProperties: false }
  )
]);
const PageDraftCandidateFields = {
  targetSlideNumber: SlideNumberSchema,
  pageSource: PageSourceTextSchema,
  sourceHash: Type.String({ pattern: "^[a-f0-9]{64}$" }),
  diagnostics: DiagnosticsSchema,
  compiledScreenshot: CompiledScreenshotSchema,
  summary: PageDraftSummarySchema
};
export const PresentationPageDraftReviewSchema = Type.Union([
  Type.Object(
    {
      operation: Type.Literal("create"),
      ...PageDraftCandidateFields,
      assignment: PresentationPageCreateAssignmentSchema,
      designReference: PresentationDesignReferenceSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      operation: Type.Literal("revise"),
      sourceSlideNumber: SlideNumberSchema,
      ...PageDraftCandidateFields,
      assignment: PresentationPageReviseAssignmentSchema,
      designReference: PresentationDesignReferenceSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      operation: Type.Literal("retain"),
      sourceSlideNumber: SlideNumberSchema,
      ...PageDraftCandidateFields
    },
    { additionalProperties: false }
  )
]);

export const PresentationPageDraftFailureSchema = Type.Object(
  {
    operation: Type.Union([Type.Literal("create"), Type.Literal("revise"), Type.Literal("retain")]),
    targetSlideNumber: SlideNumberSchema,
    sourceSlideNumber: Type.Optional(SlideNumberSchema),
    code: Type.String({ minLength: 1, maxLength: 128, pattern: "^[A-Z][A-Z0-9_]*$" }),
    retryable: Type.Boolean(),
    diagnostics: DiagnosticsSchema
  },
  { additionalProperties: false }
);
const PageDraftCountsSchema = Type.Object(
  {
    created: Type.Integer({ minimum: 0, maximum: 60 }),
    revised: Type.Integer({ minimum: 0, maximum: 60 }),
    retained: Type.Integer({ minimum: 0, maximum: 60 }),
    failed: Type.Integer({ minimum: 0, maximum: 60 }),
    cancelled: Type.Integer({ minimum: 0, maximum: 60 }),
    total: Type.Integer({ minimum: 1, maximum: 60 })
  },
  { additionalProperties: false }
);
export const PageDraftReviewBundleSchema = Type.Union([
  Type.Object(
    {
      status: Type.Literal("review_ready"),
      pages: Type.Array(PresentationPageDraftReviewSchema, { minItems: 1, maxItems: 60 }),
      counts: PageDraftCountsSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      status: Type.Literal("partial_failure"),
      pages: Type.Array(PresentationPageDraftReviewSchema, { maxItems: 60 }),
      failures: Type.Array(PresentationPageDraftFailureSchema, { minItems: 1, maxItems: 60 }),
      counts: PageDraftCountsSchema
    },
    { additionalProperties: false }
  ),
  Type.Object(
    {
      status: Type.Literal("interrupted"),
      reason: Type.Union([
        Type.Literal("steer"),
        Type.Literal("human_request"),
        Type.Literal("abort"),
        Type.Literal("timeout")
      ])
    },
    { additionalProperties: false }
  )
]);

export const CommitPresentationPagesResultSchema = Type.Object(
  {
    status: Type.Literal("committed"),
    unverifiedSlideNumbers: Type.Array(SlideNumberSchema, { maxItems: 60 }),
    changes: Type.Object(
      {
        created: Type.Integer({ minimum: 0, maximum: 60 }),
        revised: Type.Integer({ minimum: 0, maximum: 60 }),
        retained: Type.Integer({ minimum: 0, maximum: 60 }),
        moved: Type.Integer({ minimum: 0, maximum: 60 }),
        deleted: Type.Integer({ minimum: 0, maximum: 60 })
      },
      { additionalProperties: false }
    ),
    currentState: PresentationCurrentStateSchema
  },
  { additionalProperties: false }
);

export const InspectPresentationSlideResultSchema = Type.Object(
  {
    status: Type.Union([Type.Literal("valid"), Type.Literal("invalid")]),
    theme: PresentationThemeRefSchema,
    slide: PresentationSlideSummarySchema,
    previousSlides: Type.Array(PresentationSlideSummarySchema, { maxItems: 3 }),
    pageSource: PageSourceTextSchema,
    diagnostics: DiagnosticsSchema,
    omittedDiagnosticCount: Type.Optional(Type.Integer({ minimum: 1, maximum: 1_000_000 }))
  },
  { additionalProperties: false }
);

export type PresentationCheckpointHintV1 = Static<typeof PresentationCheckpointHintSchema>;
export type CompiledScreenshotV1 = Static<typeof CompiledScreenshotSchema>;
export type ListPresentationThemesInputV1 = Static<typeof ListPresentationThemesInputSchema>;
export type ListPresentationThemesResultV1 = Static<typeof ListPresentationThemesResultSchema>;
export type PresentationThemeGuideV1 = Static<typeof PresentationThemeGuideSchema>;
export type OpenPresentationInputV1 = Static<typeof OpenPresentationInputSchema>;
export type OpenPresentationResultV1 = Static<typeof OpenPresentationResultSchema>;
export type PresentationOutlineSnapshotV1 = Static<typeof PresentationOutlineSnapshotSchema>;
export type PresentationSlideSummaryV1 = Static<typeof PresentationSlideSummarySchema>;
export type PresentationAuthoringGuideV1 = Static<typeof PresentationAuthoringGuideSchema>;
export type PresentationDesignReferenceV1 = Static<typeof PresentationDesignReferenceSchema>;
export type PresentationPageCreateAssignmentV1 = Static<
  typeof PresentationPageCreateAssignmentSchema
>;
export type PresentationPageReviseAssignmentV1 = Static<
  typeof PresentationPageReviseAssignmentSchema
>;
export type PresentationPageAssignmentV1 = Static<typeof PresentationPageAssignmentSchema>;
export type PresentationPageRetainItemV1 = Static<typeof PresentationPageRetainItemSchema>;
export type PresentationPageBatchItemV1 = Static<typeof PresentationPageBatchItemSchema>;
export type DraftPresentationPagesInputV1 = Static<typeof DraftPresentationPagesInputSchema>;
export type RevisePresentationPagesInputV1 = Static<typeof RevisePresentationPagesInputSchema>;
export type CommitPresentationPagesInputV1 = Static<typeof CommitPresentationPagesInputSchema>;
export type PresentationPageDraftReviewV1 = Static<typeof PresentationPageDraftReviewSchema>;
export type PresentationPageDraftFailureV1 = Static<typeof PresentationPageDraftFailureSchema>;
export type PageDraftReviewBundleV1 = Static<typeof PageDraftReviewBundleSchema>;
export type CommitPresentationPagesResultV1 = Static<typeof CommitPresentationPagesResultSchema>;
export type InspectPresentationSlideInputV1 = Static<typeof InspectPresentationSlideInputSchema>;
export type InspectPresentationSlideResultV1 = Static<typeof InspectPresentationSlideResultSchema>;
export type PresentationCompilerDiagnosticV1 = Static<typeof PresentationCompilerDiagnosticSchema>;
export type PresentationCurrentStateV1 = Static<typeof PresentationCurrentStateSchema>;
export type CheckPresentationInputV1 = Static<typeof CheckPresentationInputSchema>;
export type CheckPresentationResultV1 = Static<typeof CheckPresentationResultSchema>;
